import React, { useEffect, useState } from 'react';
import { Theme, ThemeProps } from '@radix-ui/themes';

import {
  Container,
  Heading,
  Text,
  Grid,
  Box,
  Link,
  Avatar,
  Tooltip,
  Badge,
} from 'design-system';

import {
  IconMailbox,
  IconDeviceMobile,
  IconFileCv,
  IconBrandLinkedin,
} from '@tabler/icons-react';

import { ExperienceCard } from './components/ExperienceCard';
import './App.scss';

function App() {
  const [theme, setTheme] = useState<ThemeProps['accentColor']>('brown');

  return (
    <Theme accentColor={theme} grayColor="mauve" panelBackground="translucent">
      <Container className="app-container">
        <Grid columns={{ initial: '1', md: '2' }} gap="3" width="auto">
          <Box className="profile-box">
            <Tooltip content="Click me to reset the theme!">
              <Avatar
                className="profile-avatar"
                src="/img/avatar.jpg"
                fallback="MG"
                size="9"
                onClick={() => setTheme('brown')}
              />
            </Tooltip>
            <Heading size="9">Miguel Gatmaytan</Heading>
            <Text as="p" className="sub-header" size="5">
              Frontend Engineer
            </Text>
            <Text as="p" className="header-tagline" size="2">
              Specializing in frontend architecture and performance.
            </Text>
            <Box className="profile-badges-box">
              <Badge>Active Collaborator</Badge>
              <Badge>Over Communicator</Badge>
              <Badge>Product Focused</Badge>
              <Badge>Self Starter</Badge>
            </Box>
            <Box className="contact-information-box">
              <Link href="mailto:gatmaytan.miguel@gmail.com" size="2">
                <IconMailbox />
                <Text as="div" weight="bold">
                  gatmaytan.miguel@gmail.com
                </Text>
              </Link>
              <Link href="tel:17789549971" size="2">
                <IconDeviceMobile />
                <Text as="div" weight="bold">
                  +1 (778)-954-9971
                </Text>
              </Link>
              <Link
                href="/docs/Miguel-Gatmaytan-Resume.pdf"
                size="2"
                target="_blank"
              >
                <IconFileCv />
                <Text as="div" weight="bold">
                  Download my resume!
                </Text>
              </Link>
            </Box>
            <Box className="pokemon-box">
              <Tooltip content="The grass is always greener!">
                <img
                  src="/img/bulbasaur.gif"
                  onClick={() => setTheme('grass')}
                />
              </Tooltip>
              <Tooltip content="Light my fire!">
                <img
                  src="/img/charmander.gif"
                  onClick={() => setTheme('tomato')}
                />
              </Tooltip>
              <Tooltip content="Water is essential to life!">
                <img src="/img/squirtle.gif" onClick={() => setTheme('cyan')} />
              </Tooltip>
              <Tooltip content="I'll light up your world!">
                <img src="/img/pikachu.gif" onClick={() => setTheme('amber')} />
              </Tooltip>
              <Text size="1" as="div" className="pokemon-do-it-message">
                Which pokemon will you choose?
              </Text>
            </Box>
          </Box>
          <Box>
            <ExperienceCard
              company="Roofr"
              position="Senior Frontend Engineer"
              dates="2025 - Present"
              description="
            Build scalable, high-performance user interfaces that enhance enterprise application experiences and align with business goals. 
            Collaborate across teams to deliver impactful solutions while streamlining development workflows and ensuring clean, maintainable code."
              skills={['React', 'Typescript', 'Micro-frontends']}
            />
            <ExperienceCard
              company="Chick-Fil-A"
              position="Staff Software Engineer & Team Lead"
              dates="2023 - 2025"
              description="
            Bellwood consultant driving architectural decisions, maintaining code quality, and resolving
            anti-patterns while planning future work and supporting team
            success. Collaborating with designers and business stakeholders to
            meet requirements, rapidly develop features, fix complex bugs, and
            deploy code to production."
              skills={['React', 'Typescript', 'Micro-frontends']}
            />
            <ExperienceCard
              company="data.ai"
              position="Senior Frontend Engineer (Infrastructure)"
              dates="2021 - 2023"
              description="Enhanced application stability by working on core infrastructure and
            architecture. Improved regression testing with a revamped Storybook setup,
             Chromatic integration, and Jenkins pipeline automation with Slack alerts. 
             Established TypeScript best practices and developed a web-based Microsoft Excel 
             plugin with an efficient batching system that drastically reduced API calls."
              skills={['React', 'Typescript', 'NextJS', 'Chromatic']}
            />
            <ExperienceCard
              company="CareCru"
              position="Senior Frontend Engineer"
              dates="2020-2021"
              description="
            Led the implementation of Micro-Frontends using Webpack’s Module Federation. 
            Developed a core component library and tackled challenges associated with
             Micro-Frontend Architecture."
              skills={['React', 'Typescript', 'Apollo', 'GraphQL']}
            />
            <ExperienceCard
              company="FINCAD"
              position="Web Developer"
              dates="2017-2020"
              description="Accelerated development of FINCAD’s F3 Workstation project while 
            identifying and fixing debugging tool memory leaks. Optimized Webpack bundling 
            to cut build time in half and refactored legacy architecture for a stronger foundation. 
            Gained DevOps experience with Ansible and Jenkins, writing playbook scripts 
            for continuous deployment."
              skills={['React', 'Redux', 'Babel', 'Webpack']}
            />
            <ExperienceCard
              company={
                <Link
                  className="linkedin-link"
                  href="https://linkedin.com/in/mgatmaytan"
                  target="_blank"
                >
                  <IconBrandLinkedin />
                  Visit my LinkedIn for more
                </Link>
              }
            />
          </Box>
        </Grid>
      </Container>
    </Theme>
  );
}

export default App;
